import { analyzeCoalitions, majorityThreshold } from '@val/engine'
import { getParty } from '@val/shared'
import { useMemo, useState } from 'react'
import './sim.css'

interface Props {
  seats: Record<string, number>
  totalSeats: number
  highlightParty?: string
}

export function CoalitionBuilder({ seats, totalSeats, highlightParty }: Props) {
  const majority = majorityThreshold(totalSeats)
  const codes = useMemo(
    () =>
      Object.keys(seats)
        .filter((c) => (seats[c] ?? 0) > 0)
        .sort((a, b) => (seats[b] ?? 0) - (seats[a] ?? 0)),
    [seats],
  )
  const [picked, setPicked] = useState<Set<string>>(new Set(highlightParty ? [highlightParty] : []))

  const sum = [...picked].reduce((a, c) => a + (seats[c] ?? 0), 0)
  const analysis = useMemo(() => analyzeCoalitions(seats, totalSeats), [seats, totalSeats])

  const toggle = (c: string) =>
    setPicked((prev) => {
      const next = new Set(prev)
      next.has(c) ? next.delete(c) : next.add(c)
      return next
    })

  const minimalWithHighlight = highlightParty
    ? analysis.minimalWinning.filter((c) => c.parties.includes(highlightParty))
    : analysis.minimalWinning

  return (
    <div className="coalition">
      <div className="coalition__pick">
        {codes.map((c) => {
          const meta = getParty(c)
          const on = picked.has(c)
          return (
            <button
              type="button"
              key={c}
              className={`coalition__chip${on ? ' is-on' : ''}`}
              onClick={() => toggle(c)}
              style={
                on
                  ? { background: meta.color, color: meta.onColor, borderColor: meta.color }
                  : undefined
              }
            >
              {meta.shortName} <span className="tnum">{seats[c] ?? 0}</span>
            </button>
          )
        })}
      </div>

      <div className={`coalition__meter${sum >= majority ? ' is-majority' : ''}`}>
        <div className="coalition__bar">
          <div
            className="coalition__fill"
            style={{ width: `${Math.min(100, (sum / totalSeats) * 100)}%` }}
          />
          <div
            className="coalition__majority"
            style={{ left: `${(majority / totalSeats) * 100}%` }}
            title={`Majoritet: ${majority}`}
          />
        </div>
        <div className="coalition__count tnum">
          <strong>{sum}</strong> / {majority} mandat{' '}
          {sum >= majority ? (
            <span className="tag tag--ok">majoritet (+{sum - majority})</span>
          ) : (
            <span className="tag">{majority - sum} kvar</span>
          )}
        </div>
      </div>

      <div className="coalition__lists">
        <div>
          <div className="card__title">
            Minsta vinnande koalitioner{highlightParty ? ` med ${highlightParty}` : ''}
          </div>
          {minimalWithHighlight.length === 0 && <p className="dim">Inga.</p>}
          <ul className="coalition__mwc">
            {minimalWithHighlight.slice(0, 12).map((co) => (
              <li key={co.parties.join('+')}>
                <span className="coalition__mwc-parties">
                  {[...co.parties]
                    .sort((a, b) => getParty(a).order - getParty(b).order)
                    .map((p) => (
                      <span
                        key={p}
                        className="mini-chip"
                        style={{ background: getParty(p).color, color: getParty(p).onColor }}
                      >
                        {getParty(p).shortName}
                      </span>
                    ))}
                </span>
                <span className="tnum dim">{co.seats}</span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="card__title">Vågmästarindex (Banzhaf)</div>
          <ul className="coalition__power">
            {Object.entries(analysis.power)
              .sort((a, b) => b[1].index - a[1].index)
              .map(([code, p]) => (
                <li key={code} className={code === highlightParty ? 'is-hi' : ''}>
                  <span
                    className="mini-chip"
                    style={{ background: getParty(code).color, color: getParty(code).onColor }}
                  >
                    {getParty(code).shortName}
                  </span>
                  <span className="coalition__power-bar">
                    <span style={{ width: `${p.index * 100}%` }} />
                  </span>
                  <span className="tnum dim">{Math.round(p.index * 100)}%</span>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </div>
  )
}
